import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getDemoEmployeeCount,
  getDemoQuoteUploadUrl,
  populateDemoCustomer,
  putFileToPresignedUrl,
} from '../api/demo-customer.api'
import { DemoPopulationResult } from '../types'
import { DEMO_CONTRIBUTION_STATUS_QUERY_KEY } from './useDemoContributionStatus'
import { DEMO_POPULATION_QUERY_KEY } from './useDemoPopulationStatus'

export type DemoUploadState =
  | 'idle'
  | 'presigning'
  | 'uploading'
  | 'populating'
  | 'confirming'
  | 'success'
  | 'error'

interface Options {
  /** ID of the demo customer being populated. */
  readonly customerId: string
  /**
   * Payroll cycle from the customer's enrollment config, forwarded to
   * populate so the backend doesn't fall back to its own default.
   * benefitEffectiveDate is intentionally omitted — the backend reads it
   * from the customer's enrollment configuration.
   */
  readonly payrollCycle?: string
  /**
   * Benchmark snapshot the contribution run prices against. Null or absent
   * keeps live pricing, so nothing is sent.
   */
  readonly benchmarkSnapshotId?: number | null
  /**
   * Called once populate succeeds. The page uses it to tell when the demo
   * contribution run could not be queued (no `contributionRunJobId`).
   */
  readonly onPopulated?: (result: DemoPopulationResult) => void
}

interface Result {
  readonly uploadState: DemoUploadState
  readonly result: DemoPopulationResult | null
  readonly inlineError: string | null
  readonly handleFile: (file: File) => Promise<void>
}

/** How often to poll the member-search count while confirming (ms). */
const POLL_INTERVAL_MS = 2_000

/**
 * Maximum time (ms) to wait for the Postgres mirror to catch up after
 * `populate` has returned. If the count is still 0 after this window we
 * show success from the populate payload — the employees were created, the
 * mirror is just lagging.
 */
const CONFIRM_TIMEOUT_MS = 60_000

/**
 * Orchestrates the three-step demo quote upload flow, then polls until the
 * member-search count reflects the new population:
 *
 *   1. POST .../presigned-upload-url  — get a short-lived S3 PUT URL
 *   2. raw fetch PUT                  — stream file bytes directly to S3
 *   3. POST .../populate              — trigger employee seeding in EPS
 *   4. poll getDemoEmployeeCount      — wait for the PG mirror to catch up
 *
 * The `confirming` state replaces the upload dropzone immediately, so a
 * lagging count of 0 can never re-show the upload form during this session.
 *
 * Poll resolution rules:
 *   - count > 0               → write count into the query cache, set success
 *   - populate returned 0 EEs → nothing to wait for, set success immediately
 *   - populate failed         → stop polling, show inline error
 *   - 60 s timeout            → set success from populate payload (mirror lag)
 *
 * Error mapping:
 *   409 → "already populated" — one-shot operation, direct the user to
 *          create a new demo customer instead.
 *   400 → pass the server message through verbatim (it is already
 *          actionable: covers non-test-client and missing Shoppable sheet).
 *   Any other → generic fallback.
 */
export function useUploadAndPopulateDemoQuote({
  customerId,
  payrollCycle,
  benchmarkSnapshotId,
  onPopulated,
}: Options): Result {
  const queryClient = useQueryClient()
  const [uploadState, setUploadState] = useState<DemoUploadState>('idle')
  const [result, setResult] = useState<DemoPopulationResult | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)

  // Refs used inside the polling loop so they are always current without
  // causing the useCallback to re-create.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const giveUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const didSucceedRef = useRef(false)
  const isMountedRef = useRef(true)
  // Carries its own customerId: if the prop changes while confirming, the
  // count still belongs to the company that was populated.
  const pendingResultRef = useRef<{
    customerId: string
    count: number
  } | null>(null)
  // Identifies the active flow. A flow parked on an await is out of reach of
  // the cleanup effects below, which can only clear timers that already exist.
  // Each flow carries its id and drops out at the next checkpoint once it has
  // been superseded, so it cannot write its state, its error, or its timers
  // over whichever flow replaced it.
  const flowIdRef = useRef(0)
  // Written during render so the check below never depends on when React
  // chooses to flush the effect that bumps the flow id. A response arriving
  // before the switch is processed at all still gets through — nothing inside
  // this hook can see a change it has not been rendered with yet — which is
  // why the page clears its own per-company state on id change as well.
  const viewedCustomerRef = useRef(customerId)
  viewedCustomerRef.current = customerId

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (giveUpTimerRef.current !== null) {
      clearTimeout(giveUpTimerRef.current)
      giveUpTimerRef.current = null
    }
  }, [])

  // Clean up on unmount so lingering intervals never try to setState on an
  // unmounted component. The flag is reset on mount because StrictMode
  // double-invokes effects in development — without it the second mount
  // would run with a flag left false by the first simulated unmount.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      stopPolling()

      const pending = pendingResultRef.current
      if (pending !== null && !didSucceedRef.current) {
        pendingResultRef.current = null
        queryClient.setQueryData(
          DEMO_POPULATION_QUERY_KEY(pending.customerId),
          pending.count
        )
      }
    }
  }, [stopPolling, queryClient])

  // The page re-renders in place when the route id changes rather than
  // remounting, so a flow left over from the previous company would keep
  // polling it and keep its 'confirming'/'success' state on screen — both of
  // which outrank the new company's employeeCount in the section below.
  const renderedCustomerRef = useRef(customerId)
  useEffect(() => {
    if (renderedCustomerRef.current === customerId) return
    renderedCustomerRef.current = customerId

    stopPolling()
    // Orphans anything still in flight for the previous company.
    flowIdRef.current += 1

    // The employees really were created for the previous company, so record
    // the count against it before dropping the flow — same rationale as the
    // unmount path, and pendingResultRef carries its own customerId.
    const pending = pendingResultRef.current
    if (pending !== null && !didSucceedRef.current) {
      queryClient.setQueryData(
        DEMO_POPULATION_QUERY_KEY(pending.customerId),
        pending.count
      )
    }

    pendingResultRef.current = null
    didSucceedRef.current = false
    setUploadState('idle')
    setResult(null)
    setInlineError(null)
  }, [customerId, stopPolling, queryClient])

  /**
   * Commits a successful result: writes the confirmed count (or the populate
   * payload count when the mirror never caught up) into the React-Query cache
   * so the page reflects the real count on the next render without a refetch.
   */
  const commitSuccess = useCallback(
    (populationResult: DemoPopulationResult, confirmedCount?: number) => {
      stopPolling()
      didSucceedRef.current = true
      pendingResultRef.current = null
      setResult(populationResult)
      setUploadState('success')

      const count = confirmedCount ?? populationResult.employeesCreated
      // Write the authoritative count directly into the cache instead of
      // invalidating: an immediate refetch would still read 0 from the lagging
      // mirror and the 5-minute stale time would freeze that 0.
      queryClient.setQueryData(DEMO_POPULATION_QUERY_KEY(customerId), count)
    },
    [customerId, queryClient, stopPolling]
  )

  const handleFile = useCallback(
    async (file: File) => {
      setInlineError(null)
      setResult(null)
      didSucceedRef.current = false
      pendingResultRef.current = null

      const flowId = (flowIdRef.current += 1)
      /** False once a customer change, a newer upload, or unmount took over. */
      const isCurrentFlow = () =>
        flowIdRef.current === flowId &&
        isMountedRef.current &&
        viewedCustomerRef.current === customerId

      try {
        // ── Step 1: get presigned URL ────────────────────────────────────
        setUploadState('presigning')
        const { presignedUploadUrl } = await getDemoQuoteUploadUrl(
          customerId,
          file.name
        )
        if (!isCurrentFlow()) return

        // ── Step 2: PUT bytes directly to S3 ────────────────────────────
        setUploadState('uploading')
        await putFileToPresignedUrl(presignedUploadUrl, file)
        if (!isCurrentFlow()) return

        // ── Step 3: trigger population in EPS ───────────────────────────
        setUploadState('populating')
        const populationResult = await populateDemoCustomer(customerId, {
          fileName: file.name,
          ...(payrollCycle ? { payrollCycle } : {}),
          ...(typeof benchmarkSnapshotId === 'number'
            ? { benchmarkSnapshotId }
            : {}),
        })

        // Populate queues the demo contribution run — refetch its status now
        // so polling starts immediately. This is cache-level work that does
        // not depend on the mirror catching up, so it runs before step 4.
        void queryClient.invalidateQueries({
          queryKey: DEMO_CONTRIBUTION_STATUS_QUERY_KEY(customerId),
        })

        // Superseded while populate was in flight — the page has moved on, or
        // unmounted, so this flow must not touch the UI or notify the parent.
        //
        // The count still has to be recorded: the employees were created, the
        // cache outlives this component, and a read inside the 5-minute
        // staleTime would otherwise see the stale 0 and re-show the upload
        // form for a company that already has employees. `customerId` is this
        // flow's own, so the count always lands on the right company. This
        // mirrors the give-up path, which also falls back to the populate
        // payload's count.
        if (!isCurrentFlow()) {
          queryClient.setQueryData(
            DEMO_POPULATION_QUERY_KEY(customerId),
            populationResult.employeesCreated
          )
          return
        }

        // Reported as soon as populate succeeds rather than on confirmation:
        // whether the contribution run was queued is already known, and the
        // mirror may lag a further 60 s.
        onPopulated?.(populationResult)

        // ── Step 4: wait for the PG mirror to catch up ──────────────────
        // If populate created no employees, there is nothing to wait for.
        if (populationResult.employeesCreated === 0) {
          commitSuccess(populationResult)
          return
        }

        pendingResultRef.current = {
          customerId,
          count: populationResult.employeesCreated,
        }
        setUploadState('confirming')

        // Give-up timer: starts now (after populate returned), so a slow
        // census run doesn't consume it. Falls back to success from the
        // populate payload — the employees were definitely created.
        giveUpTimerRef.current = setTimeout(() => {
          if (!isCurrentFlow()) return
          if (!didSucceedRef.current) {
            commitSuccess(populationResult)
          }
        }, CONFIRM_TIMEOUT_MS)

        // Poll every 2 s until the mirror reports a non-zero count.
        pollIntervalRef.current = setInterval(async () => {
          try {
            const count = await getDemoEmployeeCount(customerId)
            // Clearing the interval cannot recall a request already in flight.
            if (!isCurrentFlow()) return
            if (count > 0 && !didSucceedRef.current) {
              commitSuccess(populationResult, count)
            }
          } catch {
            // Swallow poll errors — the give-up timer is the safety net.
          }
        }, POLL_INTERVAL_MS)
      } catch (err: unknown) {
        // A superseded flow failing late must not surface its error on the
        // company now on screen, nor clear the timers that flow is relying on.
        if (!isCurrentFlow()) return
        stopPolling()
        const status = (err as any)?.response?.status
        const serverMessage: string | undefined = (err as any)?.response?.data
          ?.message

        let message: string
        if (status === 409) {
          message =
            'This demo customer already has employees. Create a new demo customer instead.'
        } else if (status === 400 && serverMessage) {
          message = serverMessage
        } else if (err instanceof Error) {
          message = err.message
        } else {
          message = 'Upload failed. Please try again.'
        }

        setInlineError(message)
        setUploadState('error')
      }
    },
    [
      customerId,
      payrollCycle,
      benchmarkSnapshotId,
      commitSuccess,
      stopPolling,
      queryClient,
      onPopulated,
    ]
  )

  return { uploadState, result, inlineError, handleFile }
}
